import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { sitesSso } from '@newfold/huapi-js';

import useFilteredHostingSites from '~/hooks/useFilteredHostingSites';
import useRefetchInterval from '~/hooks/useRefetchInterval';
import { SITE_LIST_PAGE_SIZE } from '~/scenes/Sites/utils/constants';

type StoredPendingSite = {
  hostingId: number | string;
  windowName?: string;
};

export type SiteTabEntry = {
  tab?: Window | null;
  hostingId?: number | string;
  windowName?: string;
  processed?: boolean;
};

export type SiteTabRegistry = Map<number, SiteTabEntry>;

const PENDING_TABS_STORAGE_KEY = 'wp_site_tab_polling_pending';
const hasWindow = globalThis.window !== undefined;

const getSessionStorage = (): Storage | undefined => {
  if (!hasWindow) return undefined;
  return globalThis.sessionStorage ?? undefined;
};

const filterOutSiteIds = (ids: number[], idsToRemove: Set<number>): number[] =>
  ids.filter((id) => !idsToRemove.has(id));

const readStoredPendingSites = (): Record<string, StoredPendingSite> => {
  const storage = getSessionStorage();
  if (!storage) return {};
  try {
    const pendingTabsData = storage.getItem(PENDING_TABS_STORAGE_KEY);
    if (!pendingTabsData) return {};
    return JSON.parse(pendingTabsData);
  } catch {
    return {};
  }
};

const ensureTabHasName = (
  siteId: number,
  tab: Window | null,
): string | undefined => {
  if (!tab) return undefined;
  const existingName = tab.name?.trim();
  const nextName = existingName?.length
    ? existingName
    : `site-tab-${siteId}-${Date.now()}`;
  try {
    tab.name = nextName;
  } catch {
    return nextName;
  }
  return nextName;
};

/**
 * Hook to manage polling of hosting sites until specific siteIds become active,
 * and then trigger SSO into any registered tabs for those sites.
 */
const useSiteTabPolling = () => {
  const siteTabsRef = useRef<SiteTabRegistry>(new Map());
  const [pendingSiteIds, setPendingSiteIds] = useState<number[]>([]);
  const { refetchTime } = useRefetchInterval();

  const updateSiteTabRefData = useCallback(
    (siteId: number, updates: Partial<SiteTabEntry>): SiteTabEntry => {
      const prevEntry = siteTabsRef.current.get(siteId) ?? {};
      const nextEntry = { ...prevEntry, ...updates };
      siteTabsRef.current.set(siteId, nextEntry);
      return nextEntry;
    },
    [],
  );

  const updateSSOTabInSS = useCallback(() => {
    const storage = getSessionStorage();
    if (!storage) return;
    const payload: Record<string, StoredPendingSite> = {};
    for (const [siteId, { hostingId, windowName }] of siteTabsRef.current) {
      if (hostingId === undefined || hostingId === null) continue;
      payload[siteId] = {
        hostingId,
        windowName,
      };
    }
    if (Object.keys(payload).length) {
      storage.setItem(PENDING_TABS_STORAGE_KEY, JSON.stringify(payload));
    } else {
      storage.removeItem(PENDING_TABS_STORAGE_KEY);
    }
  }, []);

  const registerPendingSite = useCallback(
    (
      siteId: number,
      siteSSOWindow: Window | null,
      hostingId: string | number,
      providedWindowName?: string,
    ) => {
      setPendingSiteIds((ids) =>
        ids.includes(siteId) ? ids : [...ids, siteId],
      );

      const siteSSOTabInfo: Partial<SiteTabEntry> = {
        hostingId,
        tab: siteSSOWindow ?? undefined,
      };

      const resolvedWindowName =
        providedWindowName ?? ensureTabHasName(siteId, siteSSOWindow);
      if (resolvedWindowName) {
        siteSSOTabInfo.windowName = resolvedWindowName;
      }

      updateSiteTabRefData(siteId, siteSSOTabInfo);
      updateSSOTabInSS();
    },
    [updateSSOTabInSS, updateSiteTabRefData],
  );

  useEffect(() => {
    if (!hasWindow) return;
    const storedEntries = Object.entries(readStoredPendingSites());
    if (!storedEntries.length) return;

    setPendingSiteIds((ids) => {
      const merged = new Set(ids);
      for (const [siteIdStr] of storedEntries) {
        const siteId = Number(siteIdStr);
        if (Number.isFinite(siteId)) merged.add(siteId);
      }
      return Array.from(merged);
    });

    for (const [siteIdStr, { hostingId, windowName }] of storedEntries) {
      const siteId = Number(siteIdStr);
      if (!Number.isFinite(siteId)) continue;
      updateSiteTabRefData(siteId, { hostingId, windowName });
    }
  }, [updateSiteTabRefData]);

  // Fetch hosting sites with polling only while there are pending sites
  // Determine which hostingId to poll: pick the hostingId of the first pending site,
  const hostingIdToPoll = useMemo(() => {
    const firstPending = pendingSiteIds[0];
    if (typeof firstPending !== 'number') return undefined;
    return siteTabsRef.current.get(firstPending)?.hostingId;
  }, [pendingSiteIds]);

  const hostingSites = useFilteredHostingSites(
    hostingIdToPoll as any,
    { limit: SITE_LIST_PAGE_SIZE, page: 1, search: '' },
    {
      query: {
        enabled: Boolean(hostingIdToPoll && pendingSiteIds.length),
        staleTime: 0,
        refetchInterval: pendingSiteIds.length ? refetchTime : false,
        refetchIntervalInBackground: Boolean(pendingSiteIds.length),
      },
    },
  );

  const sites = useMemo(() => {
    const items = hostingSites?.data?.data?.items ?? [];
    return items.filter((site) => typeof site?.id === 'number');
  }, [hostingSites?.data?.data?.items]);

  const siteStatusGroups = useMemo(() => {
    const active: number[] = [];
    const failed: number[] = [];

    for (const siteId of pendingSiteIds) {
      const status = sites.find((site) => site.id === siteId)?.status;
      if (status === 'active') active.push(siteId);
      if (status === 'failed_create') failed.push(siteId);
    }

    return { active, failed };
  }, [pendingSiteIds, sites]);

  const closeTab = useCallback((siteId: number) => {
    const entry = siteTabsRef.current.get(siteId);
    entry?.tab?.close();
    siteTabsRef.current.delete(siteId);
  }, []);

  const handleSsoRedirect = useCallback(
    async (siteId: number) => {
      try {
        const response = await sitesSso(siteId);
        const ssoUrl = response?.data?.sso;
        if (!ssoUrl) return;

        const entry = siteTabsRef.current.get(siteId);
        let tab = entry?.tab ?? null;

        if ((!tab || tab.closed) && hasWindow) {
          const windowName = entry?.windowName;
          if (windowName) {
            const reopened = globalThis.open('', windowName);
            if (reopened) {
              tab = reopened;
              updateSiteTabRefData(siteId, { tab: reopened });
            }
          }
        }

        if (tab && !tab.closed) {
          tab.location.replace(ssoUrl);
        }
      } catch (error) {
        console.error(
          `[useSiteTabPolling] SSO fetch failed for site ${siteId}:`,
          error,
        );
      }
    },
    [updateSiteTabRefData],
  );

  // Handle failed sites: close their tabs and remove from pending
  useEffect(() => {
    if (!siteStatusGroups.failed.length) return;
    const failedIds = new Set(siteStatusGroups.failed);

    setPendingSiteIds((ids) => filterOutSiteIds(ids, failedIds));

    for (const siteId of siteStatusGroups.failed) {
      closeTab(siteId);
    }
    updateSSOTabInSS();
  }, [closeTab, siteStatusGroups.failed, updateSSOTabInSS]);

  // Handle active sites: process SSO redirects and remove from pending
  useEffect(() => {
    if (!siteStatusGroups.active.length) return;

    const unprocessedIds = siteStatusGroups.active.filter((siteId) => {
      const entry = siteTabsRef.current.get(siteId);
      return !entry?.processed;
    });
    if (!unprocessedIds.length) return;

    for (const siteId of unprocessedIds) {
      updateSiteTabRefData(siteId, { processed: true });
    }

    const removalSet = new Set(unprocessedIds);

    const processRedirects = async () => {
      await Promise.all(unprocessedIds.map(handleSsoRedirect));
      setPendingSiteIds((ids) => filterOutSiteIds(ids, removalSet));
      for (const siteId of unprocessedIds) {
        siteTabsRef.current.delete(siteId);
      }
      updateSSOTabInSS();
    };

    processRedirects();
  }, [
    handleSsoRedirect,
    siteStatusGroups.active,
    updateSiteTabRefData,
    updateSSOTabInSS,
  ]);

  return { registerPendingSite, siteTabsRef } as const;
};

export default useSiteTabPolling;
